import React, { useEffect, useMemo, useState } from 'react';
import BCComponentWrapper from 'design-v2/common/BCComponentWrapper/BCComponentWrapper';
import style from 'external-components/blockout-by-service-editor/style.module.css';
import ActionButton from 'design-v2/common/ActionButton';
import { Input, TextField } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { useDispatch, useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import { IDiaryService } from 'shared-types/DiaryTypes';
import Select from 'react-select';
import DateUtilsService from 'external-services/date-utils-service';
import { cloneDeep } from 'lodash-es';
import BlockOutsService from 'external-services/blockouts-service/blockouts.service';
import { IBlockout } from 'shared-types/SharedTypes';
import { setSelectedSchedule } from 'app/reducers/schedule/slice';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
let timeOutCreateBlockOut: NodeJS.Timeout;
interface IBlockoutTimePickerProps {
  exit: () => void;
  handleBlockoutTimePickerSave?: () => void;
  floorplanBlockoutsForSave: Partial<IBlockout>
}
const BlockoutTimePicker = (props: IBlockoutTimePickerProps) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [ableToSave, setAbleToSave] = useState(false);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [blockoutName, setBlockoutName] = useState('');

  const selectedSchedule = useSelector((state: DiaryRootState) => state.scheduleReducer.selectedSchedule);
  const timeZoneId = useSelector((state: DiaryRootState) => state.venueReducer.timeZoneId);
  // compensating for stupid triple 'o' variable name in diary
  // @ts-ignore
  const interval = selectedSchedule?.boookingInterval || selectedSchedule?.bookingInterval || 15;
  const venueCurrentTime = DateUtilsService.getVenueTime(timeZoneId);
  // get the earliest time
  const smallestStartTime = useMemo(() => {
    return Math.min(...selectedSchedule.services.map((e: IDiaryService) => e.startTime.getTime()))
  }, [selectedSchedule]);

  // get the latest time
  const largestEndTime = useMemo(() => {
    return Math.max(...selectedSchedule.services.map((e: IDiaryService) => e.endTime.getTime()))
  }, [selectedSchedule]);

  // get original time list
  const startTimeList = useMemo(() => {
    return DateUtilsService.getTimesBetweenStartAndEndTimes(
      smallestStartTime,
      largestEndTime,
      interval
    )
  }, [smallestStartTime, largestEndTime, interval])

  // filter by current time
  const filteredStartTimeList = useMemo(() => {
    const i = startTimeList.findIndex((t) => t.time > venueCurrentTime);
    if (i > 0) {
      return startTimeList.slice(i - 1);
    }
    return startTimeList
  }, [startTimeList]);

  // generate start time options
  const startTimeOptions = useMemo(() => {
    return filteredStartTimeList.map((t) => ({ value: t.time, label: DateUtilsService.dateToTimeStr(t.time)}))
  }, [filteredStartTimeList]);

  // generate end time options
  const endTimeOptions = useMemo(() => {
    return startTimeOptions.filter((t) => t.value > (selectedStartTime || venueCurrentTime))
  }, [startTimeOptions, selectedStartTime]);

  // update ableToSave when all value ready
  useEffect(() => {
    if (selectedStartTime && selectedEndTime) {
      setAbleToSave(true);
    }
  }, [selectedStartTime, selectedEndTime]);

  const customStyles = {
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 300,
    }),
    control: (provided: any) => ({
      ...provided,
      width: 210,
    })
  };

  const saveHandler = () => {
    if (timeOutCreateBlockOut) {
      clearTimeout(timeOutCreateBlockOut);
    }
    setSubmitting(true);
    timeOutCreateBlockOut = setTimeout(() => {
      const blockoutForSave = cloneDeep(props.floorplanBlockoutsForSave);
      blockoutForSave.name = blockoutName;
      blockoutForSave.startDateTime = selectedStartTime;
      blockoutForSave.endDateTime = selectedEndTime;
      BlockOutsService.save(blockoutForSave as IBlockout).then(() => {
        // save successful, start to exit
        setSubmitting(false);
        props.exit();// this exit should only hide time picker
        props.handleBlockoutTimePickerSave && props.handleBlockoutTimePickerSave();

        // To reset Schedule Slide, so we can get latest data for blockout
        // @ts-ignore
        dispatch(setSelectedSchedule({ useCachedScheduleLookup: false }));
      }).catch((err) => {
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Problem saving blockout',
            errorMessage: `There was an error trying to save your blockout. \n\n${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
      });
    }, 200);
  }

  return (
    <BCComponentWrapper>
      <div className={style.header} >
        <div className={style.btnWrap}>
          <ActionButton
            fullWidth={true}
            variant={'secondary'}
            size={'large'}
            text={'Cancel'}
            handleClick={props.exit}
          />
        </div>
        <span className={style.title}>Create Blockout</span>
        <div>
          <ActionButton
            fullWidth={true}
            variant={'primary'}
            size={'large'}
            text={'Create'}
            disabled={!ableToSave || submitting}
            handleClick={saveHandler}
            data-testid={`save-blockoout`}
          />
        </div>
      </div>
      <div className={style.blockoutTimePickerContent}>
        {/*<TextField label="Blockout name" fullWidth />*/}
        <div>
          <p>Blockout name</p>
          <Input fullWidth
            onChange={(e) => {
              setBlockoutName(e.target.value);
            }}
          />
        </div>
        <p style={{fontWeight: 700}}>Blockout time</p>
        <p>Select the blockout time period for the day.</p>
        <div className={style.displayInRow}>
          <AccessTimeIcon />
          <div className={style.displayInRow}>
            <span style={{marginRight: 10}}>From</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              // defaultValue={startTimeOptions[0]}
              name="From"
              styles={customStyles}
              menuPosition="fixed" // This ensures the dropdown is rendered in a portal attached to the body
              menuPortalTarget={document.body} // Attach the menu portal to the body element
              options={startTimeOptions.filter((e) => selectedEndTime ? e.value < selectedEndTime : true)}
              onChange={(e) => {
                setSelectedStartTime(e.value);
              }}
              isSearchable={false} // used to fix the issue of keyboard appears when click on the dropdown in iPad
            />
          </div>
          <div className={style.displayInRow}>
            <span style={{marginRight: 10}}>To</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              // defaultValue={endTimeOptions[0]}
              name="To"
              styles={customStyles}
              menuPosition="fixed" // This ensures the dropdown is rendered in a portal attached to the body
              menuPortalTarget={document.body} // Attach the menu portal to the body element
              options={endTimeOptions}
              onChange={(e) => {
                setSelectedEndTime(e.value);
              }}
              isSearchable={false} // used to fix the issue of keyboard appears when click on the dropdown in iPad
            />
          </div>
        </div>
      </div>
    </BCComponentWrapper>
  );
};

export default BlockoutTimePicker;
